import { useState } from 'react'
import AddTagShadcn from 'app/components/tags/AddTagShadcn'
import { Badge } from 'app/components/ui/badge'
import { Button } from 'app/components/ui/button'
import { Input } from 'app/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'app/components/ui/select'
import { useListCopy } from 'app/hooks/useListCopy'
import type { BulkAction, BulkActionValue } from './types'

type BulkActionPanelProps = {
  action: BulkAction
  count: number
  applying: boolean
  onApply: (value: BulkActionValue) => void
  onCancel: () => void
  /** Exit selection mode entirely (rendered as "Cancel" alongside Apply/Back). */
  onExit?: () => void
}

/** Collects the value for a chosen bulk action and triggers apply. */
export function BulkActionPanel({ action, count, applying, onApply, onCancel, onExit }: BulkActionPanelProps) {
  const copy = useListCopy()
  const [text, setText] = useState(``)
  const [selectValue, setSelectValue] = useState(``)
  const [tags, setTags] = useState<string[]>([])
  const [pipelineId, setPipelineId] = useState(``)
  const [stage, setStage] = useState(``)

  const value: BulkActionValue =
    action.control === `tags`
      ? tags
      : action.control === `select`
        ? selectValue
        : action.control === `pipeline`
          ? { pipelineId, stage }
          : text
  const canApply =
    action.control === `tags`
      ? tags.length > 0
      : action.control === `select`
        ? !!selectValue
        : action.control === `pipeline`
          ? !!pipelineId && !!stage
          : text.trim().length > 0

  const pipelineStages = action.pipelineOptions?.find((p) => p.id === pipelineId)?.stages ?? []

  return (
    <div className="flex w-full flex-wrap items-center gap-2 md:w-auto md:flex-nowrap md:gap-3">
      <span className="text-ui-sm font-medium text-default">{action.label}:</span>

      {action.control === `text` && (
        <Input
          autoFocus
          value={text}
          placeholder={action.placeholder ?? `Enter a value`}
          onChange={(e) => setText(e.target.value)}
          className="h-9 w-full md:w-56"
        />
      )}

      {action.control === `select` && (
        <Select value={selectValue} onValueChange={setSelectValue}>
          <SelectTrigger className="h-9 w-full md:w-56">
            <SelectValue placeholder={action.placeholder ?? `Choose…`} />
          </SelectTrigger>
          {/* Bar is docked at the bottom of the table, so open upward. */}
          <SelectContent side="top">
            {action.options?.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {action.control === `pipeline` && (
        <div className="flex w-full flex-col gap-2 md:w-auto md:flex-row md:items-center">
          <div className="flex w-full flex-col gap-1 md:w-44">
            <Select
              value={pipelineId}
              onValueChange={(v) => {
                setPipelineId(v)
                setStage(``)
              }}
            >
              <SelectTrigger className="h-9 w-full">
                <SelectValue placeholder={copy.Noun} />
              </SelectTrigger>
              <SelectContent side="top">
                {action.pipelineOptions?.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {copy.filterListsHelper && <span className="text-body-xs text-subtle">{copy.filterListsHelper}</span>}
          </div>
          <Select value={stage} onValueChange={setStage} disabled={!pipelineId}>
            <SelectTrigger className="h-9 w-full md:w-44">
              <SelectValue placeholder="Stage" />
            </SelectTrigger>
            <SelectContent side="top">
              {pipelineStages.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {action.control === `tags` && (
        <div className="flex w-full flex-wrap items-center gap-2 md:w-auto">
          <div className="flex flex-wrap items-center gap-1">
            {tags.map((tag) => (
              <Badge
                key={tag}
                label={tag}
                color="gray"
                onRemove={() => setTags((prev) => prev.filter((t) => t !== tag))}
              />
            ))}
          </div>
          <AddTagShadcn
            asLabel
            label="Choose tag"
            shouldConfirmNewTag={false}
            onAdd={(tag, color) => {
              setTags((prev) => (prev.includes(tag) ? prev : [...prev, tag]))
              // A color is only passed when the user creates a brand-new tag — persist it.
              if (color) action.onCreateTag?.(tag, color)
            }}
          />
        </div>
      )}

      <Button size="sm" disabled={!canApply || applying} onClick={() => onApply(value)}>
        {applying ? `Applying…` : `Apply to ${count}`}
      </Button>
      <Button size="sm" variant="white" disabled={applying} onClick={onCancel}>
        Back
      </Button>
      {onExit && (
        <Button size="sm" variant="white" disabled={applying} onClick={onExit} className="ml-auto md:ml-0">
          Cancel
        </Button>
      )}
    </div>
  )
}
